import { useQuery } from '@tanstack/react-query';
import { ZodSchema } from 'zod';

import { HttpError } from '../../utils/HttpError';

export enum GetEULAStatus {
  IDLE = 'idle',
  LOADING = 'loading',
  SUCCESS = 'success',
  ERROR = 'error',
}

interface FetchOptions<T> {
  primaryUrl: string;
  fallbackUrl?: string;
  schema: ZodSchema<T>;
  enabled?: boolean;
}

export function useGetExperienceFragment<T>({
  primaryUrl,
  fallbackUrl,
  schema,
  enabled = true,
}: FetchOptions<T>) {
  const query = useQuery<T>({
    queryKey: ['experience-fragment', primaryUrl, fallbackUrl],
    enabled,
    queryFn: async ({ signal }) => {
      const fetchAndValidate = async (url: string): Promise<T> => {
        const res = await fetch(url, { signal });
        if (!res.ok)
          throw new HttpError(res.status, `[EULA] HTTP ${res.status} while fetching ${url}`);

        const json = await res.json();
        const parsed = schema.safeParse(json);
        if (!parsed.success) {
          console.error('[EULA] Contract mismatch', parsed.error.format());
          throw new Error('[EULA] Invalid backend contract');
        }
        return parsed.data;
      };

      try {
        return await fetchAndValidate(primaryUrl);
      } catch (err) {
        if (fallbackUrl && fallbackUrl !== primaryUrl) return fetchAndValidate(fallbackUrl);
        throw err;
      }
    },
  });

  const status: GetEULAStatus =
    !enabled && query.fetchStatus === GetEULAStatus.IDLE
      ? GetEULAStatus.IDLE
      : query.isPending
        ? GetEULAStatus.LOADING
        : query.isError
          ? GetEULAStatus.ERROR
          : GetEULAStatus.SUCCESS;

  return {
    data: query.data,
    error: query.error ?? null,
    status,
    isFetching: query.isFetching,
    refetch: query.refetch,
  };
}
